/** @jsx jsx */
import * as React from "react";
import Emojis, { EmojisValues } from "../../components/common/Emojis/Emojis";
import { Form } from "../../components/common/Form/Form";
import Tags, { Tag } from "../../components/common/Tags/Tags";
import { consultationFeedbackFormStyles, commentBoxStyle, tabStyle } from "./consultationFeedbackFormStyle";
import { jsx } from "@emotion/core";
import Button from "../../components/common/Button/Button";
import AppUtils from "../../utils/AppUtils";
import { ApiManager } from "../../api/ApiManager";

export enum ReasonCategory {
  VIDEO = "Video",
  AUDIO = "Audio",
  CALL = "Call Experience"
}

const ratingMap = {
  [EmojisValues.VHAPPY]: 4,
  [EmojisValues.HAPPY]: 3,
  [EmojisValues.MEH]: 2,
  [EmojisValues.SAD]: 1
}

export interface ConsultationFeedbackPayload {
  consultationId: string,
  formId: string,
  rating: number,
  comment: string,
  tags: Tag[]
}

const ratings: any = {
  [EmojisValues.HAPPY]: {
    title: "What can we improve?",
    tags: {
      [ReasonCategory.VIDEO]: [
        {
          name: "I could not see the patient"
        },
        {
          name: "The patient could not see me"
        },
        {
          name: "The video quality was poor"
        }
      ],
      [ReasonCategory.AUDIO]: [
        {
          name: "I could not hear the patient"
        },
        {
          name: "The patient could not hear me"
        },
        {
          name: "There was too much disturbance"
        }
      ],
      [ReasonCategory.CALL]: [
        {
          name: "There was lag on the call"
        },
        {
          name: "Call ended unexpectedly"
        }
      ]
    },
  },
  [EmojisValues.MEH]: {
    title: "What went wrong?",
    tags: {
      [ReasonCategory.VIDEO]: [
        {
          name: "I could not see the patient"
        },
        {
          name: "The patient could not see me"
        },
        {
          name: "The video quality was poor"
        }
      ],
      [ReasonCategory.AUDIO]: [
        {
          name: "I could not hear the patient"
        },
        {
          name: "The patient could not hear me"
        },
        {
          name: "There was too much disturbance"
        }
      ],
      [ReasonCategory.CALL]: [
        {
          name: "There was lag on the call"
        },
        {
          name: "Call ended unexpectedly"
        }
      ]
    }
  },
  [EmojisValues.SAD]: {
    title: "What went wrong?",
    tags: {
      [ReasonCategory.VIDEO]: [
        {
          name: "I could not see the patient"
        },
        {
          name: "The patient could not see me"
        },
        {
          name: "The video quality was poor"
        }
      ],
      [ReasonCategory.AUDIO]: [
        {
          name: "I could not hear the patient"
        },
        {
          name: "The patient could not hear me"
        },
        {
          name: "There was too much disturbance"
        }
      ],
      [ReasonCategory.CALL]: [
        {
          name: "There was lag on the call"
        },
        {
          name: "Call ended unexpectedly"
        }
      ]
    }
  }
}

interface ConsultationFeedbackFormProps {
  consultationId: number,
  submitConsultationForm: (consultationId: number) => void;
  isFormAlreadySubmitted?: boolean
}

const formatFormDataForApi = (formData: any, consultationId: number): Promise<any> => {
  const feedbackData: ConsultationFeedbackPayload = {
    rating: ratingMap[formData.rating as EmojisValues],
    formId: "CONSULTATION_FEEDBACK_FORM",
    consultationId: consultationId.toString(),
    comment: formData.comment || "",
    tags: (formData.tags || []).map((tag: any) => tag.name)
  }
  return ApiManager.getAlbusApis().submitConsultationFeedback(feedbackData);
}


// will add interface after api integration, no props as of now
const ConsultationFeedbackForm: React.SFC<ConsultationFeedbackFormProps> = (props) => {

  const [selectedRating, setRating] = React.useState<EmojisValues | null>(null);
  const [reasonCategory, setReasonCategory] = React.useState<ReasonCategory>(ReasonCategory.VIDEO);
  const [isFormSubmitted, submitForm] = React.useState<boolean>(props.isFormAlreadySubmitted || false); 

  React.useEffect(() => {
    if (isFormSubmitted) {
      props.submitConsultationForm(props.consultationId);
    }
  }, [isFormSubmitted]);

  const onUpdateFormData = (formData: any) => {
    if (formData.rating !== selectedRating) {
      if (formData.rating === EmojisValues.VHAPPY) {
        // submit
        formatFormDataForApi(formData, props.consultationId);
        submitForm(true);
      }
      setRating(formData.rating as EmojisValues);
    }
  }

  const onSelectReasonCategory = (reasonCategoryArg: ReasonCategory) => {
    setReasonCategory(reasonCategoryArg);
  }

  const isMobile = AppUtils.getIsMobile();

  if (isFormSubmitted) {
    return (
      <div css={consultationFeedbackFormStyles.submitMsg}>
        <span>Feedback shared successfully</span>
        <img css={consultationFeedbackFormStyles.submitIcon} src="/images/icons/check.svg" />
      </div>
    );
  }

  return (
    <div css={consultationFeedbackFormStyles.container}>
      <Form onUpdateFormData={onUpdateFormData} key={"consultation-form-" + isFormSubmitted.toString()}>
        {(formProps: any) => {
          const { onUpdateField, formData } = formProps;
          return (
            <div>
              <div css={consultationFeedbackFormStyles.mainQuestionRow(isMobile)}>
                <div css={consultationFeedbackFormStyles.mainQuestionText(isMobile)}>How was the audio and video quality of the call?</div>
                <Emojis onClick={onUpdateField} name="rating" selectedRating={formData.rating} />
              </div>
              {selectedRating && selectedRating !== EmojisValues.VHAPPY && (
                <div>
                  {!isMobile && (
                    <div css={consultationFeedbackFormStyles.subQuestion}>{ratings[selectedRating].title}</div>
                  )}
                  <div css={consultationFeedbackFormStyles.tabsContainer}>
                    <div css={tabStyle(reasonCategory === ReasonCategory.VIDEO)} onClick={() => { onSelectReasonCategory(ReasonCategory.VIDEO) }}>Video</div>
                    <div css={tabStyle(reasonCategory === ReasonCategory.AUDIO)} onClick={() => { onSelectReasonCategory(ReasonCategory.AUDIO) }}>Audio</div>
                    <div css={tabStyle(reasonCategory === ReasonCategory.CALL)} onClick={() => { onSelectReasonCategory(ReasonCategory.CALL) }}>Call Experience</div>
                  </div>
                  <Tags
                    tags={ratings[selectedRating].tags[reasonCategory]}
                    name="tags"
                    onTagClick={onUpdateField}
                  />
                  <div css={consultationFeedbackFormStyles.commentSubmitContainer}>
                    <input css={commentBoxStyle} type="text" placeholder="Add Comments" onChange={(e) => onUpdateField("comment", e.target.value)} />
                    <Button onClick={() => { formatFormDataForApi(formData, props.consultationId); submitForm(true); }} >
                      Submit
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        }}
      </Form>
    </div>
  )
}

export default ConsultationFeedbackForm;